const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export type AdminRole = 'admin' | 'supervisor' | 'breakglass';

export interface AdminUserInfo {
  username: string;
  role: AdminRole;
  displayName?: string;
  mustResetPassword?: boolean;
  permissions: string[];
  expiresAt?: string;
}

export const PERMS = {
  manageVolunteersView: 'panels.manage_volunteers.view',
  manageVolunteersRead: 'panels.manage_volunteers.read',
  manageVolunteersWrite: 'panels.manage_volunteers.write',
  manageVolunteersPrint: 'panels.manage_volunteers.print_badge',
  emergencyView: 'panels.emergency_contacts.view',
  emergencyRead: 'panels.emergency_contacts.read',
  liabilityView: 'panels.liability_waivers.view',
  liabilityRead: 'panels.liability_waivers.read',
  reportsDirectoryView: 'panels.reports_directory.view',
  reportsDirectoryRead: 'panels.reports_directory.read',
  reportsTimeView: 'panels.reports_time.view',
  reportsTimeRead: 'panels.reports_time.read',
  reportsCalendarView: 'panels.reports_calendar.view',
  reportsCalendarRead: 'panels.reports_calendar.read',
  notificationsView: 'panels.notifications.view',
  notificationsRead: 'panels.notifications.read',
  settingsView: 'panels.settings.view',
  settingsManageAccess: 'panels.settings.manage_access',
  activityLogView: 'panels.activity_log.view',
  activityLogRead: 'panels.activity_log.read',
  feedbackView: 'panels.feedback.view',
  feedbackRead: 'panels.feedback.read',
} as const;

export function getAdminToken(): string | null {
  return localStorage.getItem('adminToken');
}

export function getStoredAdminUser(): AdminUserInfo | null {
  const raw = localStorage.getItem('adminUser');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminUserInfo;
  } catch {
    return null;
  }
}

export function storeAdminSession(token: string, user: AdminUserInfo): void {
  localStorage.setItem('adminToken', token);
  localStorage.setItem('adminAuthenticated', 'true');
  localStorage.setItem('adminUser', JSON.stringify(user));
}

export function clearAdminSession(): void {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminAuthenticated');
  localStorage.removeItem('adminUser');
}

export function hasPermission(perm: string, user?: AdminUserInfo | null): boolean {
  const u = user ?? getStoredAdminUser();
  return Boolean(u?.permissions?.includes(perm));
}

export function getAdminHeaders(): HeadersInit {
  const token = getAdminToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function validateAdminSession(): Promise<AdminUserInfo | null> {
  const token = getAdminToken();
  if (!token) {
    clearAdminSession();
    return null;
  }
  try {
    const response = await fetch(`${API_BASE_URL}/admin/validate-token`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok || !data.valid || !data.user) {
      clearAdminSession();
      return null;
    }
    const user: AdminUserInfo = {
      username: data.user.username,
      role: data.user.role,
      mustResetPassword: data.user.mustResetPassword,
      permissions: data.user.permissions || [],
      expiresAt: data.user.expiresAt,
    };
    localStorage.setItem('adminUser', JSON.stringify(user));
    localStorage.setItem('adminAuthenticated', 'true');
    return user;
  } catch {
    clearAdminSession();
    return null;
  }
}
